import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { FileUploadInput } from "@/components/ui/file-upload-input";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue
} from "@/components/ui/select";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger
} from "@/components/ui/tooltip";
import { HelpCircle } from "lucide-react";
import { useState, useEffect, useCallback } from "react";
import {
  useFileUploader,
  DOCUMENT_TYPES,
  DocumentsResponse,
  Document
} from "@/components/features/FileUploader";

interface WorkPermitDetailFieldsProps {
  profile: any;
  setProfile: (profile: any) => void;
  readOnly: boolean;
  loading: boolean;
}

export function WorkPermitDetailFields({
  profile,
  setProfile,
  readOnly,
  loading
}: WorkPermitDetailFieldsProps) {
  const {
    uploadTalentProfileDocument,
    getTalentDocument,
    deleteFile,
    handleViewFile
  } = useFileUploader();
  const [workPermitDocument, setWorkPermitDocument] = useState<
    Document | undefined
  >(undefined);
  const [uploading, setUploading] = useState(false);
  const [loadingDocumentRead, setLoadingDocumentRead] = useState(false);

  const fetchWorkPermitDocument = useCallback(() => {
    setLoadingDocumentRead(true);
    getTalentDocument(profile.id, DOCUMENT_TYPES.WORK_PERMIT).then(
      (res: DocumentsResponse) => {
        if (res.success) {
          setWorkPermitDocument(res.data[0]);
        }
        setLoadingDocumentRead(false);
      }
    );
  }, [profile.id, getTalentDocument]);

  const handleFileUpload = async (file: File) => {
    if (!profile) return;
    setUploading(true);
    const result = await uploadTalentProfileDocument(
      file,
      DOCUMENT_TYPES.WORK_PERMIT,
      profile.id,
      {
        values: {
          talent_id: profile.id
        }
      }
    );
    if (result.success) {
      await fetchWorkPermitDocument();
    }
    setUploading(false);
  };

  useEffect(() => {
    if (profile?.id) {
      fetchWorkPermitDocument();
    }
  }, [profile?.id, fetchWorkPermitDocument]);

  return (
    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
      {/* File upload */}
      <div className="space-y-1">
        <div className="flex items-center gap-1">
          <Label htmlFor="residence_permit">Aufenthaltstitel hochladen</Label>
          <TooltipProvider>
            <Tooltip>
              <TooltipTrigger>
                <HelpCircle className="h-4 w-4 text-gray-400" />
              </TooltipTrigger>
              <TooltipContent>
                <p>
                  Lade hier deinen Aufenthaltstitel hoch (PDF, DOC oder DOCX)
                </p>
              </TooltipContent>
            </Tooltip>
          </TooltipProvider>
        </div>
        <div className="flex items-center gap-2">
          <FileUploadInput
            currentFileName={workPermitDocument?.file_name}
            accept=".pdf,.doc,.docx"
            onChange={(e) => {
              const file = e.target.files?.[0];
              if (file) {
                handleFileUpload(file);
              }
            }}
            disabled={readOnly}
            loading={loading || loadingDocumentRead || uploading}
            onClearFile={async () => {
              if (workPermitDocument) {
                const success = await deleteFile(
                  "profiles",
                  workPermitDocument.id,
                  workPermitDocument.file_path
                );
                if (success) {
                  fetchWorkPermitDocument();
                }
              }
            }}
          />
          {workPermitDocument && (
            <Button
              variant="outline"
              size="sm"
              onClick={() =>
                handleViewFile("profiles", workPermitDocument.file_path)
              }
            >
              Anzeigen
            </Button>
          )}
        </div>
      </div>
      <div className="space-y-1">
        <div className="flex items-center gap-1">
          <Label htmlFor="residence_permit_type">Aufenthaltstitel</Label>
          <TooltipProvider>
            <Tooltip>
              <TooltipTrigger>
                <HelpCircle className="h-4 w-4 text-gray-400" />
              </TooltipTrigger>
              <TooltipContent>
                <p>Wähle die Art deines Aufenthaltstitels aus</p>
              </TooltipContent>
            </Tooltip>
          </TooltipProvider>
        </div>
        <Select
          disabled={readOnly}
          value={profile?.residence_permit_type}
          onValueChange={(value) =>
            setProfile({
              ...profile!,
              residence_permit_type: value
            })
          }
        >
          <SelectTrigger loading={loading}>
            <SelectValue placeholder="Aufenthaltstitel auswählen" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="skilled_worker_recognition">
              Fachkraft in Anerkennung (§16d AufenthG)
            </SelectItem>
            <SelectItem value="recognized_skilled_worker">
              Anerkannte Fachkraft (§18a / §18b AufenthG)
            </SelectItem>
            <SelectItem value="auxiliary_worker_recognition">
              Hilfskraft während der Anerkennung (§19c Abs. 1 i.V.m. §6 BeschV)
            </SelectItem>
            <SelectItem value="vocational_training">
              Berufsausbildung (§16a AufenthG)
            </SelectItem>
            <SelectItem value="language_course">
              Sprachkurs vor Ausbildung (§16g AufenthG)
            </SelectItem>
            <SelectItem value="eu_blue_card">EU Blaue Karte</SelectItem>
            <SelectItem value="other_employment">
              Aufenthaltserlaubnis für sonstige Beschäftigung
            </SelectItem>
            <SelectItem value="settlement_permit">
              Niederlassungserlaubnis
            </SelectItem>
            <SelectItem value="family_reunion">
              Aufenthaltserlaubnis für Familienangehörige
            </SelectItem>
            <SelectItem value="asylum_permit">
              Aufenthaltsgestattung (Asyl)
            </SelectItem>
            <SelectItem value="toleration">Duldung</SelectItem>
            <SelectItem value="other">Andere</SelectItem>
          </SelectContent>
        </Select>
      </div>

      {/* Validity date */}
      <div className="space-y-1">
        <div className="flex items-center gap-1">
          <Label htmlFor="residence_permit_validity">
            Aufenthaltserlaubnis gültig bis
          </Label>
          <TooltipProvider>
            <Tooltip>
              <TooltipTrigger>
                <HelpCircle className="h-4 w-4 text-gray-400" />
              </TooltipTrigger>
              <TooltipContent>
                <p>
                  Gib das Datum an, bis wann dein Aufenthaltstitel gültig ist
                </p>
              </TooltipContent>
            </Tooltip>
          </TooltipProvider>
        </div>
        <Input
          disabled={readOnly}
          type="date"
          id="residence_permit_validity"
          value={
            profile?.residence_permit_validity
              ? new Date(profile.residence_permit_validity)
                  .toISOString()
                  .split("T")[0]
              : ""
          }
          onChange={(e) => {
            setProfile((prev: any) => ({
              ...prev,
              residence_permit_validity: e.target.value
            }));
          }}
          className="w-full"
        />
      </div>
    </div>
  );
}
